"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.selectRadioButtonTokens = exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _RadioInput = _interopRequireDefault(require("./RadioInput"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);

// TODO: roll out a standard approach to token sets
// https://github.com/telus/universal-design-system/issues/782
const tokenKeys = ['checkedBackgroundColor', 'checkedSize', 'inputBackgroundColor', 'inputBorderColor', 'inputBorderWidth', 'inputOutlineColor', 'inputOutlineWidth', 'inputSize', 'outerBorderColor', 'outerBorderWidth', 'outerBorderGap', 'inputSize'];
const selectRadioButtonTokens = (themeTokens, prefix) => (0, _utils.selectTokens)(tokenKeys, themeTokens, prefix);
exports.selectRadioButtonTokens = selectRadioButtonTokens;
const getBorderRadius = inputSize => inputSize / 2;
const selectCheckedStyles = _ref => {
  let {
    checkedBackgroundColor,
    checkedSize
  } = _ref;
  return {
    backgroundColor: checkedBackgroundColor,
    borderRadius: checkedSize / 2,
    height: checkedSize,
    width: checkedSize
  };
};
const selectIconTokens = _ref2 => {
  let {
    icon,
    iconColor,
    checkedSize
  } = _ref2;
  return {
    icon,
    color: iconColor,
    size: checkedSize
  };
};
const selectInputIconStyles = (_ref3, isChecked) => {
  let {
    iconBackgroundColor,
    inputBackgroundColor,
    inputBorderColor,
    inputBorderWidth,
    inputSize,
    containerShadow
  } = _ref3;
  return {
    borderColor: inputBorderColor,
    borderRadius: getBorderRadius(inputSize),
    borderWidth: inputBorderWidth,
    backgroundColor: isChecked ? iconBackgroundColor : inputBackgroundColor,
    height: inputSize,
    width: inputSize,
    ...(0, _ThemeProvider.applyShadowToken)(containerShadow)
  };
};
const selectInputStyles = _ref4 => {
  let {
    inputBackgroundColor,
    inputBorderColor,
    inputBorderWidth,
    inputOutlineColor,
    inputOutlineWidth,
    inputSize
  } = _ref4;
  return {
    borderColor: inputBorderColor,
    borderRadius: getBorderRadius(inputSize),
    borderWidth: inputBorderWidth,
    backgroundColor: inputBackgroundColor,
    height: inputSize,
    width: inputSize,
    ..._Platform.default.select({
      web: {
        outlineStyle: 'solid',
        outlineColor: inputOutlineColor,
        outlineWidth: inputOutlineWidth
      }
    })
  };
};
const selectOuterBorderStyles = _ref5 => {
  let {
    outerBorderColor,
    outerBorderWidth,
    outerBorderGap,
    inputSize
  } = _ref5;
  return {
    ...(0, _ThemeProvider.applyOuterBorder)({
      outerBorderColor,
      outerBorderWidth,
      outerBorderGap,
      borderRadius: getBorderRadius(inputSize)
    })
  };
};

/**
 * The visual toggle of a radio input. Is not interactive on its own, should be used inside
 * an interactive parent that passes down props when interacted with.
 */
const RadioButton = /*#__PURE__*/_react.default.forwardRef((_ref6, ref) => {
  let {
    isChecked,
    tokens,
    inactive,
    defaultChecked,
    inputId,
    isControlled,
    handleChange,
    name: inputName,
    value,
    testID = 'Radio-Checked',
    ...rest
  } = _ref6;
  const radioTokens = (0, _ThemeProvider.useThemeTokens)('Radio', tokens, {
    checked: isChecked
  });
  const {
    icon: IconComponent,
    ...iconTokens
  } = selectIconTokens(radioTokens);
  const themeTokens = (0, _ThemeProvider.validateThemeTokens)((0, _ThemeProvider.resolveThemeTokens)(IconComponent ? radioTokens : tokens, {
    checked: isChecked
  }), (0, _utils.getTokensSetPropType)(tokenKeys), 'RadioButton');
  const getCheckedInput = checked => {
    if (!checked) return null;
    if (!IconComponent) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
        style: selectCheckedStyles(themeTokens),
        testID: testID
      });
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(IconComponent, {
      ...iconTokens
    });
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    style: selectOuterBorderStyles(themeTokens),
    ...selectProps(rest),
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
      style: [staticStyles.defaultInputStyles, IconComponent ? selectInputIconStyles(themeTokens, isChecked) : selectInputStyles(themeTokens)],
      testID: "Radio-Input",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_RadioInput.default, {
        ref: ref,
        checked: isChecked,
        defaultChecked: defaultChecked,
        disabled: inactive,
        id: inputId,
        isControlled: isControlled,
        onChange: handleChange,
        name: inputName,
        value: value
      }), getCheckedInput(isChecked)]
    })
  });
});
RadioButton.displayName = 'RadioButton';
RadioButton.propTypes = {
  ...selectedSystemPropTypes,
  isChecked: _propTypes.default.bool,
  tokens: (0, _utils.getTokensSetPropType)(tokenKeys, {
    allowFunction: true
  }),
  inactive: _propTypes.default.bool,
  defaultChecked: _propTypes.default.bool,
  inputId: _propTypes.default.string,
  isControlled: _propTypes.default.bool,
  handleChange: _propTypes.default.func,
  name: _propTypes.default.string,
  value: _propTypes.default.bool
};
const staticStyles = _StyleSheet.default.create({
  defaultInputStyles: {
    alignItems: 'center',
    justifyContent: 'center'
  }
});
var _default = exports.default = RadioButton;